import { useState, useEffect } from "react";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDateTime, formatTime as formatClockTime } from "@/lib/i18n";
import { ArrowRightLeft, GitBranch, ScrollText } from "lucide-react";

interface ExecutionLog {
  id: string;
  workflowId: string;
  workflowVersion: number;
  personId: string;
  status: string;
  currentNodeId: string | null;
  enteredAt: string;
  completedAt: string | null;
  errorCount: number;
  lastError: string | null;
}

interface ExecutionStep {
  id: string;
  nodeId: string;
  nodeName?: string;
  nodeType: string;
  status: string;
  createdAt?: string;
  executedAt?: string;
  errorMessage?: string | null;
  output: Record<string, unknown> | null;
}

type StatusFilter = "all" | "running" | "completed" | "failed";
type WorkflowSummary = { id: string; name: string };

interface TraceResponse {
  execution: {
    id: string;
    workflowVersion: number;
    contactId: string;
    correlationId: string | null;
    currentNodeId?: string | null;
  };
  definition: {
    version: number;
    nodes: Array<{ id: string; name: string; type: string; condition?: string | null }>;
    edges?: Array<{ id: string; source: string; target: string; label?: string; condition?: string }>;
  };
  steps: Array<{
    id: string;
    nodeId: string;
    nodeName: string;
    nodeType: string;
    status: string;
    output: Record<string, unknown> | null;
    errorMessage: string | null;
    executedAt: string;
    durationMs?: number | null;
    input?: Record<string, unknown> | null;
    condition?: string | null;
  }>;
}

type TraceNodeStatus = "executed" | "failed" | "skipped" | "current" | "pending";

function formatNodeLabel(value: string) {
  return value.replace(/_/g, " ");
}

function formatTime(value?: string | null) {
  if (!value) {
    return "Pending";
  }

  return formatClockTime(value);
}

function getTraceNodeStatus(
  nodeId: string,
  currentNodeId: string | null | undefined,
  stepByNodeId: Map<string, TraceResponse["steps"][number]>,
): TraceNodeStatus {
  const step = stepByNodeId.get(nodeId);
  if (!step) {
    return currentNodeId === nodeId ? "current" : "pending";
  }

  if (step.status === "failed") {
    return "failed";
  }

  if (step.status === "skipped") {
    return "skipped";
  }

  return "executed";
}

function getTraceNodeTone(status: TraceNodeStatus) {
  return {
    executed: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
    failed: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
    skipped: "border-border bg-muted/40 text-muted-foreground",
    current: "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
    pending: "border-border bg-background text-muted-foreground",
  }[status];
}

/** Workflow execution logs — list + step-by-step replay. */
export default function WorkflowLogsPage() {
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [workflowNames, setWorkflowNames] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [steps, setSteps] = useState<ExecutionStep[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [trace, setTrace] = useState<TraceResponse | null>(null);

  // Fetch logs
  useEffect(() => {
    setIsLoading(true);
    const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    Promise.all([
      apiFetch<{ data: { items: ExecutionLog[] } }>(`/crm/workflows/executions${params}`),
      apiFetch<{ data: WorkflowSummary[] }>("/crm/workflows?limit=200"),
    ])
      .then(([executionsRes, workflowsRes]) => {
        setLogs(executionsRes.data.items ?? []);
        setWorkflowNames(
          Object.fromEntries((workflowsRes.data ?? []).map((workflow) => [workflow.id, workflow.name])),
        );
      })
      .catch(() => setLogs([]))
      .finally(() => setIsLoading(false));
  }, [statusFilter]);

  // Fetch steps when log selected
  useEffect(() => {
    const selected = logs.find((log) => log.id === selectedLogId);
    if (!selectedLogId || !selected) { setSteps([]); setTrace(null); return; }
    setStepsLoading(true);
    Promise.all([
      apiFetch<{ data: ExecutionStep[] }>(`/crm/workflows/executions/${selectedLogId}/steps`),
      apiFetch<{ data: TraceResponse }>(`/crm/workflows/${selected.workflowId}/executions/${selectedLogId}/trace`),
    ])
      .then(([stepsRes, traceRes]) => {
        setTrace(traceRes.data ?? null);
        const traceSteps = new Map((traceRes.data?.steps ?? []).map((step) => [step.id, step]));
        setSteps(
          (stepsRes.data ?? []).map((step) => ({
            ...step,
            nodeName: traceSteps.get(step.id)?.nodeName,
            nodeType: traceSteps.get(step.id)?.nodeType ?? step.nodeType,
            output: traceSteps.get(step.id)?.output ?? step.output,
            errorMessage: traceSteps.get(step.id)?.errorMessage ?? step.errorMessage,
            executedAt: traceSteps.get(step.id)?.executedAt ?? step.createdAt,
          })),
        );
      })
      .catch(() => { setSteps([]); setTrace(null); })
      .finally(() => setStepsLoading(false));
  }, [selectedLogId, logs]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-foreground">Execution Logs</h1>

      <div className="flex gap-1 rounded-lg border border-border p-1">
        {(["all", "running", "completed", "failed"] as const).map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={cn("flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent")}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-md" />
        ))}</div>
      ) : logs.length === 0 ? (
        <EmptyState icon={<ScrollText className="h-8 w-8" />} title="No executions yet"
          description="Workflow executions will appear here when automations run."
          actionLabel="View automations" actionPath="/workflows/automations" />
      ) : (
        <div className="flex gap-4">
          {/* Log list */}
          <div className="flex-1 divide-y divide-border rounded-lg border border-border">
            {logs.map((log) => (
              <button key={log.id} onClick={() => setSelectedLogId(log.id)}
                className={cn("stagger-item flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent/50",
                  selectedLogId === log.id && "bg-accent")}>
                <div>
                  <p className="text-sm font-medium text-foreground">{workflowNames[log.workflowId] ?? `Workflow ${log.workflowId.slice(0, 8)}`}</p>
                  <div className="flex gap-2 text-xs text-muted-foreground">
                    <ExecStatusBadge status={log.status} />
                    <span>{formatDateTime(log.enteredAt)}</span>
                    <span>v{log.workflowVersion}</span>
                  </div>
                </div>
                {log.lastError ? <span className="text-xs text-destructive">Error</span> : null}
              </button>
            ))}
          </div>

          {/* Step replay panel */}
          {selectedLogId && (
            <div className="w-[30rem] shrink-0 rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Trace Timeline</h3>
                <span className="rounded-full border border-border px-2 py-1 text-[11px] text-muted-foreground">
                  Mounted CRM trace
                </span>
              </div>
              {trace ? (
                <div className="mb-4 rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                  <p>Contact: {trace.execution.contactId}</p>
                  <p>Workflow version: {trace.execution.workflowVersion}</p>
                  {trace.execution.correlationId ? <p>Correlation: {trace.execution.correlationId}</p> : null}
                </div>
              ) : null}
              {trace ? (
                <WorkflowTraceDefinition trace={trace} />
              ) : null}
              {stepsLoading ? (
                <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 rounded" />
                ))}</div>
              ) : steps.length === 0 ? (
                <p className="text-xs text-muted-foreground">No steps recorded</p>
              ) : (
                <div className="relative space-y-0">
                  <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
                  {steps.map((step) => (
                    <div key={step.id} className="stagger-item relative flex gap-3 py-2 pl-1">
                      <div className={cn("z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ring-2",
                        step.status === "completed" ? "bg-[var(--color-success)]/10 text-[var(--color-success)] ring-green-300"
                          : step.status === "failed" ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] ring-red-300"
                            : step.status === "skipped" ? "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] ring-gray-300"
                              : "bg-[var(--color-primary)]/10 text-[var(--color-primary)] ring-[var(--color-ring)]/50")}>
                        {step.status === "completed" ? "✓" : step.status === "failed" ? "✕" : step.status === "skipped" ? "—" : "▶"}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium">{step.nodeName ?? step.nodeType.replace(/_/g, " ")}</p>
                        <p className="text-[11px] text-muted-foreground">{step.nodeType.replace(/_/g, " ")}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatTime(step.executedAt ?? step.createdAt ?? new Date().toISOString())}
                        </p>
                        {step.errorMessage && (
                          <p className="mt-1 text-xs text-destructive">{step.errorMessage}</p>
                        )}
                        {step.output ? (
                          <div className="mt-1 inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                            <ArrowRightLeft className="h-3 w-3" />
                            Output captured
                          </div>
                        ) : null}
                        {typeof step.output === "object" && step.output ? (
                          <pre className="mt-2 overflow-x-auto rounded border border-border bg-background/80 p-2 text-[10px] text-muted-foreground">
                            {JSON.stringify(step.output, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkflowTraceDefinition({ trace }: { trace: TraceResponse }) {
  const stepByNodeId = new Map(trace.steps.map((step) => [step.nodeId, step]));

  return (
    <div className="mb-4 space-y-3 rounded-md border border-border bg-background/60 p-3" data-testid="workflow-trace-definition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Why Did This Happen?
          </p>
          <p className="text-xs text-muted-foreground">
            Mounted workflow version path with executed and skipped nodes.
          </p>
        </div>
        <span className="rounded-full border border-border px-2 py-1 text-[11px] text-muted-foreground">
          v{trace.definition.version}
        </span>
      </div>

      <div className="space-y-2">
        {trace.definition.nodes.map((node, index) => {
          const step = stepByNodeId.get(node.id);
          const status = getTraceNodeStatus(node.id, trace.execution.currentNodeId, stepByNodeId);

          return (
            <div key={node.id}>
              <div
                className={cn(
                  "rounded-md border p-3",
                  getTraceNodeTone(status),
                )}
                data-testid={`workflow-trace-node-${node.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{node.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      {formatNodeLabel(node.type)}
                    </p>
                  </div>
                  <span className="rounded-full border border-current/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
                    {status}
                  </span>
                </div>

                {node.condition ? (
                  <div className="mt-2 inline-flex items-center gap-1 rounded border border-current/20 px-2 py-1 text-[11px]">
                    <GitBranch className="h-3 w-3" />
                    <span>{node.condition}</span>
                  </div>
                ) : null}

                <div className="mt-2 grid gap-2 text-[11px] text-muted-foreground md:grid-cols-2">
                  <p>Executed: {formatTime(step?.executedAt)}</p>
                  <p>Duration: {typeof step?.durationMs === "number" ? `${step.durationMs}ms` : "n/a"}</p>
                </div>

                {step?.input ? (
                  <div className="mt-2">
                    <p className="text-[11px] font-medium text-foreground">Input</p>
                    <pre className="mt-1 overflow-x-auto rounded border border-border bg-background/70 p-2 text-[10px] text-muted-foreground">
                      {JSON.stringify(step.input, null, 2)}
                    </pre>
                  </div>
                ) : null}

                {step?.output ? (
                  <div className="mt-2">
                    <p className="text-[11px] font-medium text-foreground">Output</p>
                    <pre className="mt-1 overflow-x-auto rounded border border-border bg-background/70 p-2 text-[10px] text-muted-foreground">
                      {JSON.stringify(step.output, null, 2)}
                    </pre>
                  </div>
                ) : null}

                {step?.errorMessage ? (
                  <p className="mt-2 text-[11px] text-destructive">{step.errorMessage}</p>
                ) : null}
              </div>

              {index < trace.definition.nodes.length - 1 ? (
                <div className="flex justify-center py-1 text-muted-foreground">↓</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExecStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    running: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
    completed: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    failed: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
    cancelled: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
  };
  return <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", colors[status] ?? "bg-[var(--color-muted)]")}>{status}</span>;
}
